import React, { useState, useEffect } from "react";
import config from "../../../config";
import FundraiserList from "./FundraiserList";
import { AccountProfileFundraisers } from "../../../Interfaces/Fundraiser";
import { GeneralUserInfoResponse } from "../../../Interfaces/AccountUsers";
import "./FundraiserTabDisplay.css"; // Ensure this contains border and layout styles for consistency
import { useNavigate } from "react-router-dom";
import apiClient from "../../../services/apiClient";
import { fetchFeatureFlags } from "../../../services/fetchFeatureFlags";
import UploadAndViewAccountProfilePhoto from "../../Forms/AccountProfilePhotos/UploadAndViewAccountProfilePhoto/UploadAndViewProfilePhoto";

const BASE_API = config.API_BASE_URL;

interface Tab {
  label: string;
  type: "participant" | "rep" | "coord" | "teamAdmin";
  id?: number;
  account_profile_image_url?: string;
}

interface FundraiserTabsProps {
  accountID: number;
}

const FundraiserTabs: React.FC<FundraiserTabsProps> = ({ accountID }) => {
  const [activeTab, setActiveTab] = useState<Tab | null>(null);
  const [accountInfo, setAccountInfo] = useState<GeneralUserInfoResponse>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [participantFundraisers, setParticipantFundraisers] = useState<
    AccountProfileFundraisers[]
  >([]);
  const navigate = useNavigate();

  const separateFundraisers = (fundraisers: AccountProfileFundraisers[]) => {
    const today = new Date();
    const pastFundraisers: AccountProfileFundraisers[] = [];
    const activeFundraisers: AccountProfileFundraisers[] = [];

    fundraisers.forEach((fundraiser) => {
      const endDate = new Date(fundraiser.fundraiser.end_date);
      if (endDate < today) {
        pastFundraisers.push(fundraiser);
      } else {
        activeFundraisers.push(fundraiser);
      }
    });

    return { pastFundraisers, activeFundraisers };
  };
  const [flags, setFlags] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const loadFeatureFlags = async () => {
      const flagData = await fetchFeatureFlags();
      setFlags(flagData);
    };

    loadFeatureFlags();
  }, []);

  useEffect(() => {
    const fetchGeneralUserInfo = async (id: number) => {
      try {
        const response = await apiClient.get(
          `${BASE_API}account/general-dashboard/${id}`
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching the account info", error);
        setError("Failed to fetch account info");
        return null;
      }
    };

    const getAccountInfo = async () => {
      setLoading(true);
      const data = await fetchGeneralUserInfo(accountID);
      if (data) {
        setAccountInfo(data);
        // Setting the default tab to be the first available tab
        if (data.account_profiles && data.account_profiles.length > 0) {
          setActiveTab({
            label: `${data.account_profiles[0].first_name} ${data.account_profiles[0].last_name}`,
            type: "participant",
            id: data.account_profiles[0].id,
            account_profile_image_url:
              data.account_profiles[0].account_profile_image_url,
          });
        } else if (data.rep && data.rep.length > 0) {
          setActiveTab({
            label: "Representatives",
            type: "rep",
          });
        } else if (data.coord && data.coord.length > 0) {
          setActiveTab({
            label: "Coordinators",
            type: "coord",
          });
        } else if (data.team_admin && data.team_admin.length > 0) {
          setActiveTab({
            label: "Team Admins",
            type: "teamAdmin",
          });
        }
      }
      setLoading(false);
    };

    getAccountInfo();
  }, [accountID]);

  useEffect(() => {
    const fetchParticipantFundraisers = async (participantId: number) => {
      setLoading(true);
      try {
        const response = await apiClient.get(
          `${BASE_API}account/general-dashboard/account-profile/${participantId}`
        );
        setParticipantFundraisers(response.data);
      } catch (error) {
        console.error("Error fetching participant fundraisers", error);
        setError("Failed to fetch participant fundraisers");
      } finally {
        setLoading(false);
      }
    };

    if (activeTab && activeTab.type === "participant" && activeTab.id) {
      fetchParticipantFundraisers(activeTab.id);
    }
  }, [activeTab]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  let fundraisers: AccountProfileFundraisers[] = [];
  if (activeTab) {
    if (activeTab.type === "participant") {
      fundraisers = participantFundraisers;
    } else if (activeTab.type === "rep") {
      fundraisers = accountInfo?.rep || [];
    } else if (activeTab.type === "coord") {
      fundraisers = accountInfo?.coord || [];
    } else if (activeTab.type === "teamAdmin") {
      fundraisers = accountInfo?.team_admin || [];
    } else {
      throw new Error("Fundraiser Tab received unexpected arguments");
    }
  }

  const { activeFundraisers, pastFundraisers } =
    separateFundraisers(fundraisers);

  const tabs: Tab[] = accountInfo
    ? [
        ...accountInfo.account_profiles.map((profile) => ({
          label: `${profile.first_name} ${profile.last_name}`,
          type: "participant" as const,
          id: profile.id,
          account_profile_image_url: profile.account_profile_image_url,
        })),
        ...(accountInfo.rep?.length > 0
          ? [{ label: "Representatives", type: "rep" as const }]
          : []),
        ...(accountInfo.coord?.length > 0
          ? [{ label: "Coordinators", type: "coord" as const }]
          : []),
        ...(accountInfo.team_admin?.length > 0
          ? [{ label: "Team Admins", type: "teamAdmin" as const }]
          : []),
      ]
    : [];

  return (
    <div className="user-profile-fundraiser-tab-display-container">
      {tabs.length > 0 ? (
        <div className="custom-tabs-wrapper">
          <ul className="custom-tabs">
            {tabs.map((tab) => (
              <li
                key={tab.label}
                className={`custom-tab-item ${
                  activeTab?.label === tab.label ? "active" : ""
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.label}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="user-profile-fundraiser-tab-display-empty-tab-placeholder"></div>
      )}
      {tabs.length === 0 ? (
        <div className="user-profile-fundraiser-tab-display-no-fundraisers-container">
          <p>
            No fundraisers found. Do you have a group that needs fundraising?
          </p>
          <div
            className="user-profile-fundraiser-tab-display-clickable-div"
            onClick={() => {
              navigate("/");
            }}
          >
            Learn about Starting a Fundraiser
          </div>
          <div
            className="user-profile-fundraiser-tab-display-clickable-div"
            onClick={() => {
              navigate("/");
            }}
          >
            Learn about Joining a Fundraiser
          </div>
        </div>
      ) : (
        <div>
          {flags.participant_profile_photo && activeTab?.id && (
            <UploadAndViewAccountProfilePhoto
              selectedAccountProfileIdInt={activeTab.id}
              {...(activeTab.account_profile_image_url && {
                account_profile_image_url: activeTab.account_profile_image_url,
              })}
            />
          )}
          <FundraiserList
            fundraisers={activeFundraisers}
            fundraiserStatus="Active"
            fundraiserType={activeTab?.type}
          />
          <FundraiserList
            fundraisers={pastFundraisers}
            fundraiserStatus="Completed"
            fundraiserType={activeTab?.type}
          />
        </div>
      )}
    </div>
  );
};

export default FundraiserTabs;
